import { parseLocalDateString } from './parseLocalDate';
import {
  getBookingDurationInHours,
  getBookingNumberOfGuests,
  combineBookingDateAndTime,
} from './bookingPayload';

/**
 * Frontend mirror of the backend Lambda `calculatePricing`
 * (createBookingSecure / confirmBookingPayment). The server is authoritative;
 * this function must produce the SAME number cent-for-cent so the displayed
 * estimate matches the charge and the bookingServerAmountSync gate stays quiet.
 *
 * Lambda rules mirrored here:
 *  - billableHours = Math.max(durationHours, minNumberOfHours || 1)  (HOURLY)
 *                  = packageDurationHours                            (PACKAGES)
 *  - weekend surcharge BEFORE discount; weekday in Europe/Berlin from date+startTime
 *      PERCENTAGE => base * v/100 ; FIXED => usePackagePricing ? v : v * billableHours
 *  - discounts ONLY for HOURLY (!usePackagePricing); rules sorted by minHours DESC,
 *      first rule with billableHours >= minHours wins
 *      PERCENTAGE clamped 0..100 of (base+surcharge) ; FIXED = min(v*billableHours, studioPrice)
 *  - guests for tier selection use the SAME logic the payload sends to the server
 *      (getBookingNumberOfGuests => upper end of the people range, NO capacity cap)
 */

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const DAY_BY_SHORT_NAME = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const zeroResult = () => ({
  base: 0,
  billableHours: 0,
  appliedDiscount: 0,
  appliedSurcharge: 0,
  appliedSurchargeMeta: null,
  appliedFees: [],
  discountMinHoursApplied: null,
  discountTypeApplied: null,
  discountValueApplied: null,
  totalStudioPrice: 0,
  serviceFee: 0,
  totalPrice: 0,
  pricingUnavailable: false,
  pricingUnavailableReason: null,
});

const unavailableResult = (reason) => ({
  ...zeroResult(),
  pricingUnavailable: true,
  pricingUnavailableReason: reason,
});

export function calculateStudioPrice({ bookingStudioData, bookingData }) {
  const {
    pricingMode,
    pricePerHour,
    packages,
    discountRules = [],
    extraFees = [],
    weekendSurcharge,
    tieredPricing = [],
    minNumberOfHours,
  } = bookingStudioData || {};

  const { date, startTime, endTime, packageId, selectedPeopleGroupKey } = bookingData || {};

  // Mirror the Lambda's `usePackagePricing`: PACKAGES mode, OR a valid packageId
  // that matches one of the studio's packages even when pricingMode is missing
  // (defensive parity for legacy/incomplete studio data).
  const isPackagePricing =
    pricingMode === 'PACKAGES' ||
    (!!packageId && Array.isArray(packages) && packages.some((p) => p.id === packageId));
  const discounts = Array.isArray(discountRules) ? discountRules : [];

  // ----- 1) Base studio price + billableHours (hourly vs packages) -----
  let base = 0;
  let billableHours = 0;

  if (!isPackagePricing) {
    if (!startTime || !endTime) {
      return zeroResult();
    }

    const durationHours = getBookingDurationInHours(startTime, endTime);
    // Backend: billableHours = Math.max(durationHours, minNumberOfHours || 1)
    billableHours = Math.max(durationHours, Number(minNumberOfHours) || 1);

    // Effective hourly price: flat pricePerHour, overridden by the matching
    // tiered-pricing tier for the booked number of guests.
    let effectivePricePerHour = Number(pricePerHour) || 0;

    if (Array.isArray(tieredPricing) && tieredPricing.length > 0) {
      // Mirror the server: guests = upper end of the selected range (no capacity cap),
      // identical to getBookingNumberOfGuests used to build the booking payload.
      const guestsRaw = getBookingNumberOfGuests({ bookingData, isPackagePricing: false });
      const guests = typeof guestsRaw === 'number' && guestsRaw > 0 ? guestsRaw : 1;

      const matchingTier = tieredPricing.find((tier) => {
        const min = typeof tier.minPersons === 'number' ? tier.minPersons : parseInt(tier.minPersons || '0', 10);
        const max = typeof tier.maxPersons === 'number' ? tier.maxPersons : parseInt(tier.maxPersons || '0', 10);
        return guests >= min && guests <= max;
      });

      if (matchingTier && matchingTier.pricePerHour != null) {
        effectivePricePerHour = Number(matchingTier.pricePerHour) || effectivePricePerHour;
      } else if (effectivePricePerHour <= 0) {
        // No matching tier AND no valid flat fallback → backend would throw.
        return unavailableResult('Keine Preisstufe für diese Personenanzahl gefunden.');
      }
    }

    if (effectivePricePerHour <= 0) {
      return unavailableResult('Studio Preis ist nicht korrekt hinterlegt.');
    }

    base = roundMoney(effectivePricePerHour * billableHours);
  } else {
    const pkg = Array.isArray(packages) ? packages.find((p) => p.id === packageId) : null;
    if (!pkg) {
      return zeroResult();
    }

    const packageDurationHours = Number(pkg.durationHours);
    if (!Number.isFinite(packageDurationHours) || packageDurationHours <= 0) {
      return unavailableResult('Dieses Paket hat keine gültige Dauer.');
    }
    billableHours = packageDurationHours;

    const prices = Array.isArray(pkg.prices) ? pkg.prices : [];
    if (prices.length === 0) {
      return unavailableResult('Für dieses Paket ist kein Preis hinterlegt.');
    }

    // Mirror the server: pick the tier whose [minPersons, maxPersons] contains the
    // booked guest count (upper end of selectedPeopleGroupKey). If no tier matches,
    // block pricing instead of showing a fallback amount the Lambda would reject.
    const guestsRaw = getBookingNumberOfGuests({ bookingData, isPackagePricing: true });
    const guests = typeof guestsRaw === 'number' && guestsRaw > 0 ? guestsRaw : 1;

    let priceEntry = prices.find((p) => {
      const pMin = typeof p.minPersons === 'number' ? p.minPersons : parseInt(p.minPersons || '0', 10);
      const pMax = typeof p.maxPersons === 'number' ? p.maxPersons : parseInt(p.maxPersons || '0', 10);
      return guests >= pMin && guests <= pMax;
    });

    // selectedPeopleGroupKey kept as a secondary signal for exact-boundary matches.
    if (!priceEntry && selectedPeopleGroupKey) {
      const [minStr, maxStr] = String(selectedPeopleGroupKey).split('-');
      const min = parseInt(minStr || '0', 10);
      const max = parseInt(maxStr || '0', 10);
      priceEntry = prices.find((p) => {
        const pMin = typeof p.minPersons === 'number' ? p.minPersons : parseInt(p.minPersons || '0', 10);
        const pMax = typeof p.maxPersons === 'number' ? p.maxPersons : parseInt(p.maxPersons || '0', 10);
        return pMin === min && pMax === max;
      });
    }

    if (!priceEntry) {
      return unavailableResult('Preis für diese Personengruppe nicht verfügbar.');
    }

    const tierPrice = Number(priceEntry?.price);
    if (!Number.isFinite(tierPrice) || tierPrice <= 0) {
      return unavailableResult('Ungültiger Paketpreis für diese Personengruppe.');
    }

    base = roundMoney(tierPrice);
  }

  // ----- 2) Weekend surcharge (BEFORE discount, Europe/Berlin) -----
  let surchargeAmount = 0;
  let appliedSurchargeMeta = null;

  if (date && weekendSurcharge) {
    // Mirror the backend: determine the weekday in Europe/Berlin from the booking
    // START datetime (date + startTime), not date-only midnight, so late-night /
    // DST-edge bookings resolve to the same Berlin weekday the server uses.
    let dt = null;
    try {
      dt = startTime ? new Date(combineBookingDateAndTime(date, startTime)) : parseLocalDateString(date);
    } catch (error) {
      dt = parseLocalDateString(date);
    }

    if (dt && !Number.isNaN(dt.getTime())) {
      const localDayStr = dt.toLocaleString('en-US', { weekday: 'short', timeZone: 'Europe/Berlin' });
      const day = DAY_BY_SHORT_NAME[localDayStr] ?? dt.getDay();
      const ws = weekendSurcharge;

      let type;
      let value = 0;
      let dayLabel = '';

      if (day === 6 && ws.saturdayEnabled) {
        type = ws.saturdaySurchargeType;
        value = Number(ws.saturdaySurchargeValue) || 0;
        dayLabel = 'Samstag';
      } else if (day === 0 && ws.sundayEnabled) {
        type = ws.sundaySurchargeType;
        value = Number(ws.sundaySurchargeValue) || 0;
        dayLabel = 'Sonntag';
      }

      if (value > 0) {
        const isPercentage = type === 'PERCENTAGE' || type === 'percentage';
        if (isPercentage) {
          surchargeAmount = roundMoney((base * value) / 100);
        } else {
          // FIXED: once for packages, per billable hour for hourly (matches Lambda).
          surchargeAmount = roundMoney(isPackagePricing ? value : value * billableHours);
        }
        appliedSurchargeMeta = {
          dayLabel,
          type: isPercentage ? 'PERCENTAGE' : 'FIXED',
          configValue: value,
          isPackagePricing,
        };
      }
    }
  }

  // studioPrice after surcharge = the base the Lambda applies discounts to.
  const studioPriceAfterSurcharge = roundMoney(base + surchargeAmount);

  // ----- 3) Long-booking discounts (HOURLY only) -----
  let discountAmount = 0;
  let discountMinHoursApplied = null;
  let discountTypeApplied = null;
  let discountValueApplied = null;

  if (!isPackagePricing && Array.isArray(discounts) && discounts.length > 0 && billableHours > 0) {
    // Sorted by minHours DESC; first rule whose threshold is met wins (matches Lambda).
    const sorted = [...discounts].sort(
      (a, b) => (parseInt(b.minHours || '0', 10) || 0) - (parseInt(a.minHours || '0', 10) || 0)
    );
    const matchingRule = sorted.find((rule) => billableHours >= (parseInt(rule.minHours || '0', 10) || 0));

    if (matchingRule) {
      const value = Number(matchingRule.discountValue) || 0;
      const typeRaw = (matchingRule.discountType || '').toString().toUpperCase();
      const isPercentage = typeRaw === 'PERCENTAGE';

      if (isPercentage) {
        const clampedPercent = Math.min(Math.max(value, 0), 100);
        discountAmount = roundMoney((studioPriceAfterSurcharge * clampedPercent) / 100);
      } else {
        const totalFixedDiscount = roundMoney(value * billableHours);
        discountAmount = roundMoney(Math.min(totalFixedDiscount, studioPriceAfterSurcharge));
      }

      discountAmount = Math.min(discountAmount, studioPriceAfterSurcharge);

      discountMinHoursApplied = parseInt(matchingRule.minHours || '0', 10) || null;
      discountTypeApplied = isPercentage ? 'PERCENTAGE' : 'FIXED';
      discountValueApplied = value;
    }
  }

  // ----- 4) Extra fees (shown separately, not part of studio subtotal) -----
  // Mirror the backend: only include fees with amount > 0.
  // A zero or negative fee would diverge the displayed total from the server total
  // and could trip the FE amount gate into a confusing reconfirmation loop.
  const appliedFees = Array.isArray(extraFees)
    ? extraFees
        .filter((f) => Number(f.amount) > 0)
        .map((f) => ({
          id: f.id,
          description: f.name || f.description || 'Gebühr',
          amount: roundMoney(f.amount),
        }))
    : [];

  // ----- 5) Studio subtotal (base + surcharge - discount, WITHOUT fees) -----
  let studioPriceFinal = roundMoney(studioPriceAfterSurcharge - discountAmount);
  if (studioPriceFinal < 0) studioPriceFinal = 0;
  const totalStudioPrice = studioPriceFinal;

  return {
    base,
    billableHours,
    appliedDiscount: roundMoney(discountAmount),
    appliedSurcharge: roundMoney(surchargeAmount),
    appliedSurchargeMeta,
    appliedFees,
    discountMinHoursApplied,
    discountTypeApplied,
    discountValueApplied,
    totalStudioPrice,
    pricingUnavailable: false,
    pricingUnavailableReason: null,
    // Legacy fields for backward compatibility (overridden by useBookingPricingSync).
    serviceFee: 0,
    totalPrice: totalStudioPrice,
  };
}
